/**
 * Tracker API Client
 * Handles all communication with the tracker backend
 */

// API base URLs - relative path works with vite proxy (dev) and cloudflare tunnel (prod)
const API_BASE = '/api/work';  // Legacy routes (auth, agents, comments)
const PROJECT_API_BASE = '/api';  // Project-centric routes

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Use PROJECT_API_BASE for project-centric routes, API_BASE for legacy tracker routes
  const base = endpoint.startsWith('/projects/') ? PROJECT_API_BASE : API_BASE;
  const url = `${base}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Include cookies for auth
  });

  if (!response.ok) {
    const error = await response.text();
    throw new ApiError(response.status, error || response.statusText);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Type definitions
export interface ProjectConfig {
  id: string;
  name: string;
  display_name: string;
  description: string;
  path: string;
  github_repo?: string;
  boards: string[];
  created_at: string;
  updated_at: string;
}

/** @deprecated Use ProjectConfig instead */
export type WorktreeConfig = ProjectConfig;

export interface DocumentationLinks {
  overview: string | null;
  technical_spec: string | null;
  related_docs: string[];
}

export interface FigmaDesignRef {
  url: string;
  file_key: string;
  node_id: string;
  node_name?: string;
  type: 'frame' | 'component' | 'component_set' | 'section';
  code_connect?: {
    component_path: string;
    component_name: string;
    figma_tsx_path?: string;
  };
}

export interface Links {
  pr: string | null;
  related_issues: string[];
  external: string[];
  design?: FigmaDesignRef[];
}

export interface QualityInfo {
  coverage: number;
  checklist: string[];
}

export interface TimeTracking {
  estimated_hours: number | null;
  logged_hours: number;
}

export interface Issue {
  id: string;
  project_id: string;
  board_id: string;
  type: 'epic' | 'story' | 'task' | 'bug' | 'chore';
  title: string;
  description: string;
  status: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  parent_id: string | null;
  assignee: {
    id: string;
    name: string;
    avatar: string;
  } | null;
  labels: string[];
  quality: QualityInfo;
  time_tracking: TimeTracking;
  documentation: DocumentationLinks;
  links: Links;
  metadata: {
    created_at: string;
    updated_at: string;
    version: number;
  };

  // Phase 1.1: Data Model Extensions
  initiative_id?: string | null;
  team_ids?: string[];
  story_points?: number | null;
  epic_id?: string | null;
  release_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  estimated_duration?: number | null;
  dependencies?: {
    blocks: string[];
    blocked_by: string[];
    related: string[];
  };
  watchers?: string[];
}

export interface BoardColumn {
  id: string;
  name: string;
  color: string;
  wip_limit: number | null;
  issue_ids: string[];
}

export interface Board {
  id: string;
  project_id: string;
  name: string;
  description: string;
  $schema?: string;
  version: number;
  sprint: {
    id: string;
    name: string;
    start_date: string;
    end_date: string;
  } | null;
  columns: BoardColumn[];
  created_at: string;
  updated_at: string;
}

export interface ASTMetadata {
  headings: { level: number; text: string }[];
  code_blocks: { language: string; count: number }[];
  links: { internal: string[]; external: string[] };
  has_timeline: boolean;
  has_callouts: boolean;
  word_count: number;
}

export interface Documentation {
  id: string;
  project_id: string;
  issue_id?: string;
  epic_id?: string;
  board_id?: string;
  type: 'overview' | 'technical_spec' | 'api_reference' | 'guide' | 'troubleshooting';
  title: string;
  content: string;
  content_format?: 'markdown' | 'html';
  raw_markdown_path?: string;
  ast_metadata?: ASTMetadata;
  tags: string[];
  links: {
    related_issues: string[];
    related_docs: string[];
    external_links: string[];
  };
  metadata: {
    created_at: string;
    updated_at: string;
    author: string;
    version: number;
  };
}

export interface Comment {
  id: string;
  issue_id: string;
  author: {
    id: string;
    name: string;
    avatar: string;
  };
  content: string;
  created_at: string;
  updated_at: string;
}

export interface AgentSession {
  session_id: string;
  agent_type: 'coder' | 'tester' | 'reviewer' | 'researcher' | 'architect';
  issue_id: string;
  worktree: string;
  started_at: string;
  ended_at?: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  progress: number;
  current_task: string;
  logs: Array<{
    timestamp: string;
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
  }>;
  summary?: string;
}

// Issues API
export const issuesApi = {
  list: async (params?: {
    status?: string;
    type?: string;
    parent_id?: string;
    assignee?: string;
  }): Promise<Issue[]> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.append(key, value);
      });
    }
    const query = searchParams.toString();
    const response = await request<{ issues: Issue[]; count: number }>(
      `/issues${query ? `?${query}` : ''}`
    );
    return response.issues || [];
  },

  get: (id: string): Promise<Issue> => {
    return request<Issue>(`/issues/${id}`);
  },

  create: (data: {
    title: string;
    type?: 'epic' | 'story' | 'task' | 'bug';
    status?: string;
    parent_id?: string | null;
    description?: string;
    labels?: string[];
    priority?: 'critical' | 'high' | 'medium' | 'low';
  }): Promise<Issue> => {
    return request<Issue>('/issues', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (id: string, data: Partial<Issue>): Promise<Issue> => {
    return request<Issue>(`/issues/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateStatus: (id: string, status: string): Promise<Issue> => {
    return request<Issue>(`/issues/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  delete: (id: string): Promise<void> => {
    return request<void>(`/issues/${id}`, {
      method: 'DELETE',
    });
  },

  move: (id: string, status: string, index?: number): Promise<Issue> => {
    return request<Issue>(`/issues/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ status, index }),
    });
  },

  getAgents: async (id: string): Promise<AgentSession[]> => {
    const response = await request<{ sessions: AgentSession[]; count: number }>(
      `/issues/${id}/agents`
    );
    return response.sessions || [];
  },
};

// Project Boards API
export const projectBoardsApi = {
  list: async (projectId: string): Promise<Board[]> => {
    const response = await request<{ boards: Board[]; count: number }>(
      `/projects/${projectId}/work/boards`
    );
    return response.boards || [];
  },

  get: (projectId: string, boardId: string): Promise<Board> => {
    return request<Board>(`/projects/${projectId}/work/boards/${boardId}`);
  },

  create: (projectId: string, data: {
    name: string;
    description: string;
    columns?: BoardColumn[];
  }): Promise<Board> => {
    return request<Board>(`/projects/${projectId}/work/boards`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (projectId: string, boardId: string, data: Partial<Board>): Promise<Board> => {
    return request<Board>(`/projects/${projectId}/work/boards/${boardId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: (projectId: string, boardId: string): Promise<void> => {
    return request<void>(`/projects/${projectId}/work/boards/${boardId}`, {
      method: 'DELETE',
    });
  },
};

// Legacy alias for backward compatibility
export const boardsApi = projectBoardsApi;

// Board API (legacy - backward compatibility)
export const boardApi = {
  get: (boardId?: string): Promise<Board> => {
    const endpoint = boardId ? `/board/${boardId}` : '/board/default';
    return request<Board>(endpoint);
  },
};

// Comments API
export const commentsApi = {
  list: async (issueId: string): Promise<Comment[]> => {
    const response = await request<{ comments: Comment[]; count: number }>(
      `/issues/${issueId}/comments`
    );
    return response.comments || [];
  },

  create: (issueId: string, text: string, author: { id: string; name: string; avatar: string }): Promise<Comment> => {
    return request<Comment>(`/issues/${issueId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text, author }),
    });
  },

  update: (commentId: string, issueId: string, text: string): Promise<Comment> => {
    return request<Comment>(`/comments/${commentId}?issue_id=${issueId}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    });
  },

  delete: (commentId: string, issueId: string): Promise<void> => {
    return request<void>(`/comments/${commentId}?issue_id=${issueId}`, {
      method: 'DELETE',
    });
  },
};

// Agents API
export const agentsApi = {
  listActive: async (): Promise<AgentSession[]> => {
    const response = await request<{ sessions: AgentSession[]; count: number }>(
      '/agents/sessions'
    );
    return response.sessions || [];
  },

  get: (sessionId: string): Promise<AgentSession> => {
    return request<AgentSession>(`/agents/sessions/${sessionId}`);
  },

  start: (data: {
    agent_type: 'coder' | 'tester' | 'reviewer' | 'researcher' | 'architect';
    issue_id: string;
    worktree: string;
    initial_task: string;
  }): Promise<AgentSession> => {
    return request<AgentSession>('/agents/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (sessionId: string, data: {
    progress?: number;
    current_task?: string;
    status?: 'active' | 'paused';
  }): Promise<AgentSession> => {
    return request<AgentSession>(`/agents/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  end: (sessionId: string, status: 'completed' | 'failed', summary?: string): Promise<AgentSession> => {
    return request<AgentSession>(`/agents/sessions/${sessionId}/end`, {
      method: 'POST',
      body: JSON.stringify({ status, summary }),
    });
  },
};

// Auth API
export const authApi = {
  getStatus: (): Promise<{
    authenticated: boolean;
    user?: {
      id: string;
      name: string;
      avatar: string;
    };
  }> => {
    return request<any>('/auth/status');
  },

  logout: (): Promise<void> => {
    return request<void>('/auth/logout', {
      method: 'POST',
    });
  },
};

// Documentation API
export const documentationApi = {
  list: async (projectId: string, params?: { search?: string }): Promise<Documentation[]> => {
    const searchParams = new URLSearchParams();
    if (params?.search) {
      searchParams.append('search', params.search);
    }
    const query = searchParams.toString();
    const response = await request<{ documentation: Documentation[]; count: number }>(
      `/projects/${projectId}/work/documentation${query ? `?${query}` : ''}`
    );
    return response.documentation || [];
  },

  get: (projectId: string, docId: string): Promise<Documentation> => {
    return request<Documentation>(`/projects/${projectId}/work/documentation/${docId}`);
  },

  create: (projectId: string, data: {
    issue_id?: string;
    epic_id?: string;
    board_id?: string;
    type: Documentation['type'];
    title: string;
    content: string;
    tags?: string[];
    author: string;
  }): Promise<Documentation> => {
    return request<Documentation>(`/projects/${projectId}/work/documentation`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (projectId: string, docId: string, data: Partial<Documentation>): Promise<Documentation> => {
    return request<Documentation>(`/projects/${projectId}/work/documentation/${docId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: (projectId: string, docId: string): Promise<void> => {
    return request<void>(`/projects/${projectId}/work/documentation/${docId}`, {
      method: 'DELETE',
    });
  },

  linkToDoc: (projectId: string, sourceDocId: string, targetDocId: string): Promise<Documentation> => {
    return request<Documentation>(`/projects/${projectId}/work/documentation/${sourceDocId}/link`, {
      method: 'POST',
      body: JSON.stringify({ target_doc_id: targetDocId }),
    });
  },

  linkToIssue: (projectId: string, docId: string, issueId: string): Promise<Documentation> => {
    return request<Documentation>(`/projects/${projectId}/work/documentation/${docId}/link`, {
      method: 'POST',
      body: JSON.stringify({ issue_id: issueId }),
    });
  },

  // Issue-specific documentation
  getForIssue: async (projectId: string, issueId: string): Promise<Documentation[]> => {
    const response = await request<{ documentation: Documentation[]; count: number }>(
      `/projects/${projectId}/work/issues/${issueId}/documentation`
    );
    return response.documentation || [];
  },

  createForIssue: (projectId: string, issueId: string, data: {
    type: Documentation['type'];
    title: string;
    content: string;
    tags?: string[];
    author: string;
  }): Promise<Documentation> => {
    return request<Documentation>(`/projects/${projectId}/work/issues/${issueId}/documentation`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Markdown upload methods
  uploadMarkdown: async (
    projectId: string,
    file: File,
    metadata: {
      type: Documentation['type'];
      title?: string;
      issue_id?: string;
      epic_id?: string;
      board_id?: string;
      tags?: string[];
      related_issues?: string[];
      related_docs?: string[];
      author: string;
    }
  ): Promise<{ documentation: Documentation; validationErrors: string[]; warnings?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', metadata.type);
    formData.append('author', metadata.author);

    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.issue_id) formData.append('issue_id', metadata.issue_id);
    if (metadata.epic_id) formData.append('epic_id', metadata.epic_id);
    if (metadata.board_id) formData.append('board_id', metadata.board_id);
    if (metadata.tags?.length) formData.append('tags', metadata.tags.join(','));
    if (metadata.related_issues?.length) formData.append('related_issues', metadata.related_issues.join(','));
    if (metadata.related_docs?.length) formData.append('related_docs', metadata.related_docs.join(','));

    const response = await fetch(`${PROJECT_API_BASE}/projects/${projectId}/work/documentation/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      // Note: Don't set Content-Type header - browser will set it with boundary for multipart
    });

    if (!response.ok) {
      const error = await response.text();
      throw new ApiError(response.status, error || response.statusText);
    }

    return response.json();
  },

  getRawMarkdown: async (projectId: string, docId: string): Promise<string> => {
    const response = await fetch(`${PROJECT_API_BASE}/projects/${projectId}/work/documentation/${docId}/raw`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.text();
      throw new ApiError(response.status, error || response.statusText);
    }

    return response.text();
  },

  updateMarkdown: async (
    projectId: string,
    docId: string,
    file: File
  ): Promise<{ documentation: Documentation; validationErrors: string[]; warnings?: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${PROJECT_API_BASE}/projects/${projectId}/work/documentation/${docId}/markdown`, {
      method: 'PUT',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.text();
      throw new ApiError(response.status, error || response.statusText);
    }

    return response.json();
  },
};

// Project Issues API (for multi-board support)
export const projectIssuesApi = {
  list: async (projectId: string, boardId: string, params?: {
    status?: string;
    type?: string;
    parent_id?: string;
    assignee?: string;
  }): Promise<Issue[]> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.append(key, value);
      });
    }
    const query = searchParams.toString();
    const response = await request<{ issues: Issue[]; count: number }>(
      `/projects/${projectId}/work/boards/${boardId}/issues${query ? `?${query}` : ''}`
    );
    return response.issues || [];
  },

  get: (projectId: string, boardId: string, issueId: string): Promise<Issue> => {
    return request<Issue>(`/projects/${projectId}/work/boards/${boardId}/issues/${issueId}`);
  },

  create: (projectId: string, boardId: string, data: {
    title: string;
    type?: 'epic' | 'story' | 'task' | 'bug';
    status?: string;
    parent_id?: string | null;
    description?: string;
    labels?: string[];
    priority?: 'critical' | 'high' | 'medium' | 'low';
  }): Promise<Issue> => {
    return request<Issue>(`/projects/${projectId}/work/boards/${boardId}/issues`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (projectId: string, boardId: string, issueId: string, data: Partial<Issue>): Promise<Issue> => {
    return request<Issue>(`/projects/${projectId}/work/boards/${boardId}/issues/${issueId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: (projectId: string, boardId: string, issueId: string): Promise<void> => {
    return request<void>(`/projects/${projectId}/work/boards/${boardId}/issues/${issueId}`, {
      method: 'DELETE',
    });
  },

  move: (projectId: string, boardId: string, issueId: string, status: string, index?: number): Promise<Issue> => {
    return request<Issue>(`/projects/${projectId}/work/boards/${boardId}/issues/${issueId}/move`, {
      method: 'POST',
      body: JSON.stringify({ status, index }),
    });
  },
};

// Legacy alias for backward compatibility
export const hierarchicalIssuesApi = projectIssuesApi;

// Activity API
export interface ActivityEntry {
  id: string;
  issue_id: string;
  timestamp: string;
  actor: {
    id: string;
    name: string;
  };
  action: 'created' | 'updated' | 'commented' | 'linked' | 'status_changed' | 'assigned';
  changes?: Array<{
    field: string;
    old_value: any;
    new_value: any;
  }>;
  metadata?: Record<string, any>;
}

export const activityApi = {
  getActivity: async (
    projectId: string,
    issueId: string,
    limit?: number
  ): Promise<ActivityEntry[]> => {
    const params = limit ? `?limit=${limit}` : '';
    return request<ActivityEntry[]>(
      `/projects/${projectId}/work/issues/${issueId}/activity${params}`
    );
  },

  getActivitySince: async (
    projectId: string,
    issueId: string,
    since: string
  ): Promise<ActivityEntry[]> => {
    return request<ActivityEntry[]>(
      `/projects/${projectId}/work/issues/${issueId}/activity?since=${since}`
    );
  },
};

// Phase 1.1: New Types
export interface Initiative {
  id: string;
  project_id: string;
  name: string;
  description: string;
  color: string;
  status: 'planning' | 'active' | 'completed' | 'archived';
  owner: {
    id: string;
    name: string;
    avatar: string;
  } | null;
  epic_ids: string[];
  metadata: {
    created_at: string;
    updated_at: string;
    start_date: string | null;
    end_date: string | null;
  };
}

export interface Team {
  id: string;
  project_id: string;
  name: string;
  description: string;
  color: string;
  members: Array<{
    id: string;
    name: string;
    avatar: string;
  }>;
  metadata: {
    created_at: string;
    updated_at: string;
  };
}

export interface InitiativeStats {
  total_epics: number;
  total_stories: number;
  total_tasks: number;
  completed_percentage: number;
  by_status: Record<string, number>;
  team_distribution: Record<string, number>;
}

export interface TeamStats {
  total_issues: number;
  active_issues: number;
  completed_issues: number;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
  velocity: {
    current_sprint: number;
    average: number;
  };
}

export interface BoardStatistics {
  total_issues: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
  by_assignee: Record<string, number>;
  cycle_time: {
    average: number;
    median: number;
  };
  throughput: {
    last_week: number;
    last_month: number;
  };
}

export interface CreateInitiativeRequest {
  name: string;
  description: string;
  color?: string;
  status?: Initiative['status'];
  owner?: {
    id: string;
    name: string;
    avatar: string;
  };
  epic_ids?: string[];
  start_date?: string;
  end_date?: string;
}

export interface UpdateInitiativeRequest {
  name?: string;
  description?: string;
  color?: string;
  status?: Initiative['status'];
  owner?: {
    id: string;
    name: string;
    avatar: string;
  } | null;
  epic_ids?: string[];
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateTeamRequest {
  name: string;
  description: string;
  color?: string;
  members?: Array<{
    id: string;
    name: string;
    avatar: string;
  }>;
}

export interface UpdateTeamRequest {
  name?: string;
  description?: string;
  color?: string;
  members?: Array<{
    id: string;
    name: string;
    avatar: string;
  }>;
}

// Initiatives API
export const initiativesApi = {
  list: async (projectId: string): Promise<Initiative[]> => {
    const response = await request<{ initiatives: Initiative[]; count: number }>(
      `/projects/${projectId}/work/epics`
    );
    return response.initiatives || [];
  },

  get: (projectId: string, id: string): Promise<Initiative> => {
    return request<Initiative>(`/projects/${projectId}/work/epics/${id}`);
  },

  create: (projectId: string, data: CreateInitiativeRequest): Promise<Initiative> => {
    return request<Initiative>(`/projects/${projectId}/work/epics`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (projectId: string, id: string, data: UpdateInitiativeRequest): Promise<Initiative> => {
    return request<Initiative>(`/projects/${projectId}/work/epics/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: (projectId: string, id: string): Promise<void> => {
    return request<void>(`/projects/${projectId}/work/epics/${id}`, {
      method: 'DELETE',
    });
  },

  getStats: (projectId: string, id: string): Promise<InitiativeStats> => {
    return request<InitiativeStats>(`/projects/${projectId}/work/epics/${id}/stats`);
  },
};

// Teams API
export const teamsApi = {
  list: async (projectId: string): Promise<Team[]> => {
    const response = await request<{ teams: Team[]; count: number }>(
      `/projects/${projectId}/work/teams`
    );
    return response.teams || [];
  },

  get: (projectId: string, id: string): Promise<Team> => {
    return request<Team>(`/projects/${projectId}/work/teams/${id}`);
  },

  create: (projectId: string, data: CreateTeamRequest): Promise<Team> => {
    return request<Team>(`/projects/${projectId}/work/teams`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (projectId: string, id: string, data: UpdateTeamRequest): Promise<Team> => {
    return request<Team>(`/projects/${projectId}/work/teams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: (projectId: string, id: string): Promise<void> => {
    return request<void>(`/projects/${projectId}/work/teams/${id}`, {
      method: 'DELETE',
    });
  },

  getStats: (projectId: string, id: string): Promise<TeamStats> => {
    return request<TeamStats>(`/projects/${projectId}/work/teams/${id}/stats`);
  },
};

// Design API
export const designApi = {
  attach: (projectId: string, issueId: string, figmaUrl: string, nodeName?: string) =>
    request(`/projects/${projectId}/work/issues/${issueId}/design`, {
      method: 'POST',
      body: JSON.stringify({ figma_url: figmaUrl, node_name: nodeName }),
    }),
  detach: (projectId: string, issueId: string, nodeId: string) =>
    request<void>(`/projects/${projectId}/work/issues/${issueId}/design/${nodeId}`, {
      method: 'DELETE',
    }),
};

// Flat Issues API (no boardId required — queries across all boards)
export const flatIssuesApi = {
  list: async (
    projectId: string,
    params?: {
      status?: string;
      type?: string;
      epic_id?: string;
      board_id?: string;
      assignee?: string;
      initiative_id?: string;
      team_id?: string;
      release_id?: string;
    }
  ): Promise<{ issues: Issue[]; count: number }> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.append(key, value);
      });
    }
    const query = searchParams.toString();
    return request<{ issues: Issue[]; count: number }>(
      `/projects/${projectId}/work/issues${query ? `?${query}` : ''}`
    );
  },

  get: (projectId: string, issueId: string): Promise<Issue> => {
    return request<Issue>(`/projects/${projectId}/work/issues/${issueId}`);
  },

  update: (projectId: string, issueId: string, data: Partial<Issue>): Promise<Issue> => {
    return request<Issue>(`/projects/${projectId}/work/issues/${issueId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// Global Issue Lookup (no projectId required)
export const globalIssueApi = {
  get: (issueId: string): Promise<Issue> => {
    return request<Issue>(`/issues/${issueId}`);
  },
};

// Statistics API
export const statisticsApi = {
  getBoardStats: async (projectId: string, boardId: string): Promise<BoardStatistics> => {
    return request<BoardStatistics>(`/projects/${projectId}/work/boards/${boardId}/stats`);
  },
};

export { ApiError };
